import { Collection } from "@/lib/collections";
import { ArtworkModalItem } from "@/components/sections/ArtworkModal";

const FALLBACK_FORMAT = "PNG";
const KNOWN_FORMATS = new Set(["PSD", "PNG", "CANVA", "3D", "AI", "JPG"]);

const normalizeFormat = (format?: string | null): ArtworkModalItem["format"] => {
  if (!format) {
    return FALLBACK_FORMAT;
  }
  const normalized = format.toString().toUpperCase();
  return (KNOWN_FORMATS.has(normalized) ? normalized : FALLBACK_FORMAT) as ArtworkModalItem["format"];
};

const toBoolean = (value: unknown): boolean | undefined => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    if (value.toLowerCase() === "true") return true;
    if (value.toLowerCase() === "false") return false;
  }
  return undefined;
};

const toNumberOrNull = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const coerceImageSource = (
  item: Record<string, unknown>,
  index: number,
  collection: Collection
): string | null => {
  const directImage = (item.image as string) ?? (item.thumbnailUrl as string) ?? (item.thumbnail_url as string);
  if (directImage) {
    return directImage;
  }
  const previewAtIndex = collection.previews?.[index];
  if (previewAtIndex) {
    return previewAtIndex;
  }
  return collection.cover ?? null;
};

export const mapCollectionToArtworkModalItems = (collection: Collection): ArtworkModalItem[] => {
  const rawArtworks = Array.isArray(collection.artworks) ? collection.artworks : [];

  if (rawArtworks.length) {
    return rawArtworks
      .map((rawItem, index) => {
        const item = rawItem as Record<string, unknown>;
        const image = coerceImageSource(item, index, collection);
        if (!image) {
          return null;
        }

        const id = (item.id ?? `${collection.slug}-art-${index}`).toString();
        const title = (item.title as string) || `${collection.title} #${index + 1}`;
        const normalizedFormat = normalizeFormat(item.format as string | null);

        const premiumFlag = toBoolean(item.isPremium);
        const freeFlag = toBoolean(item.isFree);

        const isPremium = premiumFlag ?? (freeFlag === false ? true : collection.isPremium ?? false);
        const isFree = freeFlag ?? !isPremium;

        return {
          id,
          title,
          image,
          format: normalizedFormat,
          isPremium,
          isFree,
          fileSize: toNumberOrNull(item.fileSize ?? item.file_size),
          dominantColor: undefined,
          tags: Array.isArray(item.tags) ? (item.tags as string[]) : [],
          collectionSlug: (item.collectionSlug as string) ?? (item.collection_slug as string) ?? collection.slug ?? null
        } satisfies ArtworkModalItem;
      })
      .filter((artwork): artwork is ArtworkModalItem => Boolean(artwork));
  }

  const fallbackPreviews = collection.previews?.filter(Boolean) ?? [];

  if (fallbackPreviews.length) {
    return fallbackPreviews.map((preview, index) => ({
      id: `${collection.slug}-preview-${index}`,
      title: `${collection.title} #${index + 1}`,
      image: preview,
      format: FALLBACK_FORMAT,
      isPremium: collection.isPremium,
      isFree: !collection.isPremium,
      fileSize: null,
      dominantColor: undefined,
      collectionSlug: collection.slug
    } satisfies ArtworkModalItem));
  }

  if (collection.cover) {
    return [
      {
        id: `${collection.slug}-cover`,
        title: collection.title,
        image: collection.cover,
        format: FALLBACK_FORMAT,
        isPremium: collection.isPremium,
        isFree: !collection.isPremium,
        fileSize: null,
        dominantColor: undefined,
        collectionSlug: collection.slug
      }
    ];
  }

  return [];
};

export const resolveCollectionPreviews = (
  collection: Collection,
  artworks: ArtworkModalItem[]
): string[] => {
  const manualPreviews = collection.previews?.filter(Boolean) ?? [];
  if (manualPreviews.length) {
    return manualPreviews;
  }

  const fromArtworks = artworks.map((art) => art.image).filter(Boolean);
  if (fromArtworks.length) {
    return fromArtworks;
  }

  return collection.cover ? [collection.cover] : [];
};
